import React from 'react';
import TimeRangePicker from './ui/TimeRangePicker';

type TimePickerBlockProps = {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    isValid: boolean;
    minHour: number;
    maxHour: number;
    required?: boolean;
};

const TimePickerBlock: React.FC<
    TimePickerBlockProps & { className?: string }
> = ({
    value,
    onChange,
    onBlur,
    isValid,
    minHour,
    maxHour,
    required = true,
    className = 'flex-1',
}) => (
    <div className={className}>
        <div className="flex items-end gap-2">
            <div className="flex-1">
                <TimeRangePicker
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isValid={isValid}
                    className=""
                    required={required}
                    minHour={minHour}
                    maxHour={maxHour}
                />
            </div>
        </div>
        {!isValid && value ? (
            <p className="text-xs text-red-500 mt-1">Invalid format or end time not after start time.</p>
        ) : null}
    </div>
);

/** Shared select state for Period in the edit schedule modal */
export interface ClassPeriodSelectState {
    selectValue: string;
    showOrphan: boolean;
    orphanPeriodId: number | null;
}

export type EditScheduleItemTimeSectionProps =
    | (TimePickerBlockProps & {
          variant: 'timeOnly';
      })
    | (TimePickerBlockProps & {
          variant: 'class';
          academicYearRangeLabel: string;
          classEditPeriodSelect: ClassPeriodSelectState;
          editFormPeriodOptions: { id: number; label: string }[];
          resolvedEditPeriodLabelForOrphan: string;
          onPeriodChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
          periodSelectionDisabled?: boolean;
          periodSelectionDisabledReason?: string;
          dayValue: string;
          activeDays: string[];
          onDayChange: (
              e:
                  | React.ChangeEvent<HTMLSelectElement>
                  | React.ChangeEvent<HTMLInputElement>
                  | React.ChangeEvent<HTMLTextAreaElement>
          ) => void;
      });

const EditScheduleItemTimeSection: React.FC<EditScheduleItemTimeSectionProps> = (
    props
) => {
    if (props.variant === 'timeOnly') {
        return (
            <TimePickerBlock
                value={props.value}
                onChange={props.onChange}
                onBlur={props.onBlur}
                isValid={props.isValid}
                minHour={props.minHour}
                maxHour={props.maxHour}
                required={props.required}
                className="flex-1"
            />
        );
    }

    const {
        academicYearRangeLabel,
        classEditPeriodSelect,
        editFormPeriodOptions,
        resolvedEditPeriodLabelForOrphan,
        onPeriodChange,
        periodSelectionDisabled = false,
        periodSelectionDisabledReason = '',
        dayValue,
        activeDays,
        onDayChange,
        value,
        onChange,
        onBlur,
        isValid,
        minHour,
        maxHour,
        required,
    } = props;

    return (
        <div className="flex flex-1 flex-wrap gap-4 min-w-0">
            {/* Period */}
            <div className="flex-none w-56 min-w-[14rem]">
                <div className="flex items-center gap-1">
                    <label htmlFor="edit-period" className="block text-sm font-medium text-gray-700">
                        Period
                    </label>
                    <div className="relative group">
                        <svg
                            className="w-4 h-4 text-gray-400 cursor-help"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-10">
                            Choose which period defines week ranges and how dates are derived for this entry.
                            Use “Academic year” when no named period applies.
                        </div>
                    </div>
                </div>
                <select
                    id="edit-period"
                    name="period_id"
                    value={classEditPeriodSelect.selectValue}
                    onChange={onPeriodChange}
                    disabled={periodSelectionDisabled}
                    title={periodSelectionDisabled ? periodSelectionDisabledReason : undefined}
                    className={`w-full mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm ${periodSelectionDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                >
                    {!periodSelectionDisabled ? (
                        <option value="">Academic year ({academicYearRangeLabel})</option>
                    ) : null}
                    {classEditPeriodSelect.showOrphan && classEditPeriodSelect.orphanPeriodId !== null ? (
                        <option value={String(classEditPeriodSelect.orphanPeriodId)}>
                            {resolvedEditPeriodLabelForOrphan ||
                                `Period ${classEditPeriodSelect.orphanPeriodId}`}
                        </option>
                    ) : null}
                    {editFormPeriodOptions.map((o) => (
                        <option key={o.id} value={String(o.id)}>
                            {o.label}
                        </option>
                    ))}
                </select>
                {periodSelectionDisabled && periodSelectionDisabledReason ? (
                    <p className="mt-1 text-xs text-gray-500">{periodSelectionDisabledReason}</p>
                ) : null}
            </div>

            {/* Day */}
            <div className="flex-none w-32">
                <label htmlFor="edit-day" className="block text-sm font-medium text-gray-700 mb-1">
                    Day
                </label>
                <select
                    id="edit-day"
                    name="day"
                    value={dayValue}
                    onChange={onDayChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm capitalize"
                >
                    {activeDays.map((day) => (
                        <option key={day} value={day} className="capitalize">
                            {day}
                        </option>
                    ))}
                </select>
            </div>

            {/* Time */}
            <TimePickerBlock
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                isValid={isValid}
                minHour={minHour}
                maxHour={maxHour}
                required={required}
                className="flex-1 min-w-[12rem]"
            />
        </div>
    );
};

export default EditScheduleItemTimeSection;
